// src/utils/analytics.ts
// Centralized analytics event tracking for GA4 + Google Ads
import type { TransferType, ServiceType } from "@/types";

type GtagEvent = {
  [key: string]: string | number | boolean | undefined;
};

const getGtag = (): ((...args: any[]) => void) | null => {
  if (typeof window !== "undefined" && (window as any).gtag) {
    return (window as any).gtag;
  }
  return null;
};

/** Builds a standard GA4 ecommerce item object for vehicle-based events. */
const buildCheckoutItem = (
  vehicleId: string,
  vehicleName: string,
  price: number,
  transferType: TransferType,
  serviceType: ServiceType
) => ({
  item_id: vehicleId,
  item_name: vehicleName,
  item_category: transferType,
  item_variant: serviceType,
  price,
  quantity: 1
});

/** Track phone call link clicks */
export const trackPhoneClick = (phoneNumber: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "phone_click", {
      phone_number: phoneNumber,
      event_category: "contact"
    });
  }
};

/** Track WhatsApp link clicks */
export const trackWhatsAppClick = (phoneNumber: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "whatsapp_click", {
      phone_number: phoneNumber,
      event_category: "contact"
    });
  }
};

/** Track email link clicks */
export const trackEmailClick = (email: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "email_click", {
      email_address: email,
      event_category: "contact"
    });
  }
};

/** Track hotel booking card clicks (SLS vs Rosewood) */
export const trackBookingCardClick = (hotel: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "booking_card_click", {
      hotel,
      event_category: "engagement"
    });
  }
};

/** Track payment failure for GA4 reporting */
export const trackPaymentFailure = (orderId: string, errorCode: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "payment_failure", {
      order_id: orderId,
      error_code: errorCode,
      event_category: "booking"
    });
  }
};

/** Track payment retry attempts */
export const trackPaymentRetry = (orderId: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "payment_retry", {
      order_id: orderId,
      event_category: "booking"
    });
  }
};

/** Fire a generic GA4 event */
export const trackEvent = (eventName: string, params?: GtagEvent) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", eventName, params);
  }
};

/** Track SPA route changes (replaces disabled send_page_view in index.html) */
export const trackPageView = (path: string, title?: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href
    });
  }
};

/** Track booking wizard step impressions for funnel analysis */
export const trackWizardStep = (stepNumber: number, stepName: string) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "wizard_step_view", {
      step_number: stepNumber,
      step_name: stepName,
      event_category: "booking_funnel"
    });
  }
};

/** Track service type selection (fires on user click, not on default mount) */
export const trackServiceSelection = (transferType: TransferType, serviceType: ServiceType) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "service_selected", {
      transfer_type: transferType,
      service_type: serviceType,
      event_category: "booking_funnel"
    });
  }
};

/** GA4 view_item_list — all vehicles shown on the selection screen */
export const trackViewItemList = (
  items: Array<{
    item_id: string;
    item_name: string;
    item_category: string;
    item_variant: string;
    price: number;
  }>,
  listId: string,
  listName: string
) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "view_item_list", {
      item_list_id: listId,
      item_list_name: listName,
      currency: "USD",
      items
    });
  }
};

/** GA4 select_item + add_to_cart — fires when user picks a vehicle.
 *  NOTE: currency is kept as 'USD' for reporting consistency with historical data;
 *  Fiserv actually processes BSD (044). Changing to 'BSD' would split GA4 revenue trends. */
export const trackVehicleSelect = (
  vehicleId: string,
  vehicleName: string,
  price: number,
  transferType: TransferType,
  serviceType: ServiceType
) => {
  const gtag = getGtag();
  if (gtag) {
    const item = buildCheckoutItem(vehicleId, vehicleName, price, transferType, serviceType);
    gtag("event", "select_item", {
      item_list_id: "vehicle_selection",
      item_list_name: "Vehicle Selection",
      items: [item]
    });
    gtag("event", "add_to_cart", {
      currency: "USD",
      value: price,
      items: [item]
    });
  }
};

/** GA4 begin_checkout — fires on Payment step mount */
export const trackBeginCheckout = (
  vehicleId: string,
  vehicleName: string,
  price: number,
  transferType: TransferType,
  serviceType: ServiceType
) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "begin_checkout", {
      currency: "USD",
      value: price,
      items: [buildCheckoutItem(vehicleId, vehicleName, price, transferType, serviceType)]
    });
  }
};

/** GA4 add_payment_info — fires when user clicks "Proceed to Payment" (before Fiserv redirect) */
export const trackAddPaymentInfo = (
  vehicleId: string,
  vehicleName: string,
  price: number,
  transferType: TransferType,
  serviceType: ServiceType
) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "add_payment_info", {
      currency: "USD",
      value: price,
      payment_type: "credit_card",
      items: [buildCheckoutItem(vehicleId, vehicleName, price, transferType, serviceType)]
    });
  }
};

/** GA4 purchase — replaces raw gtag call in BookingSuccess.tsx */
export const trackPurchase = (params: {
  transactionId: string;
  value: number;
  vehicleId: string;
  vehicleName: string;
  transferType: TransferType;
  serviceType: ServiceType;
  bookingSource: string;
}) => {
  const gtag = getGtag();
  if (gtag) {
    gtag("event", "purchase", {
      transaction_id: params.transactionId,
      value: params.value,
      currency: "USD",
      items: [
        {
          ...buildCheckoutItem(
            params.vehicleId,
            params.vehicleName,
            params.value,
            params.transferType,
            params.serviceType
          ),
          item_brand: params.bookingSource
        }
      ]
    });
  }
};
